import { OrderItemService } from '@core/order/order-item.service';
import {
  CMembership,
  CMembershipRenewal,
  IMembershipDetails,
  IMembershipUpgradesInfo,
  TManageOrderType
} from '@app/tickets/tickets.model';
import { IProduct } from '@core/product/models/product.model';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import { Injectable } from '@angular/core';
import { INumberIncrementProductItem } from '@core/form/number-increment/number-increment-product-item.model';
import { LoggerService } from '@core/logger/logger.service';

@Injectable({
  providedIn: 'root'
})
export class OrderMembershipService {
  constructor(
    private orderItemService: OrderItemService,
    private loggerService: LoggerService
  ) {}

  buildUpgradedOrderItem(
    product: IProductInfo,
    parentProduct: IProduct,
    upgrades: IMembershipUpgradesInfo[],
    editMembershipItem: IOrderItem
  ): IOrderItem {
    const increment: INumberIncrementProductItem = { id: product.id, quantity: 1, product, additionalGuests: 0 };
    const item = this.orderItemService.buildOrderItem(increment, parentProduct, new Date(), undefined, []);
    const upgradeCost = this.getUpgradeCost(item, editMembershipItem, upgrades);
    return this.getUpgradedOrderItem(item, editMembershipItem, upgradeCost);
  }

  getOrderItemsForRenewal(
    productIds: string[],
    parentProduct: IProduct,
    renewalBookingUniqueId: string | undefined
  ): IOrderItem[] {
    const orderItems: IOrderItem[] = [];

    productIds?.forEach((id: string) => {
      const product = parentProduct.products.find((item: IProductInfo) => item.id === id);
      if (!product) {
        return;
      }

      const incrementProductItem = { id, quantity: 1, product, isIncrease: true, additionalGuests: 0 };
      const orderItem = this.orderItemService.buildOrderItem(
        incrementProductItem,
        parentProduct,
        new Date(),
        undefined,
        []
      );

      orderItems.push({
        ...orderItem,
        manageBookingType: CMembershipRenewal,
        manageMembership: {
          existingBookingUniqueId: renewalBookingUniqueId
        }
      });
    });

    return orderItems;
  }

  getOrderItemsFromMemberships(
    memberships: IMembershipDetails[],
    parentProducts: IProduct[],
    upgrades: IMembershipUpgradesInfo[],
    manageType: TManageOrderType
  ): IOrderItem[] {
    const orderItems: IOrderItem[] = [];

    memberships.forEach((membership: IMembershipDetails) => {
      const parentProduct = parentProducts.find(
        (product: IProduct) => product.id === membership.parentProductId.toString()
      );
      const product = parentProduct?.products.find(
        (product: IProductInfo) => product.id === membership.productId.toString()
      );

      if (!parentProduct || !product) {
        return;
      }

      if (!product) {
        return;
      }

      const isUpgradesAvailable = upgrades.find(
        (item: IMembershipUpgradesInfo) => item.bookingItemPartId === membership.bookingItemPartId
      );

      // The membership booking item part ID is heavily tied through existing flows based on whether it's set or not for upgrade/renewal scenarios
      const setBookingItemPartId: boolean = manageType === CMembership ? !!isUpgradesAvailable : true;
      const increment = {
        id: membership.productId.toString(),
        quantity: 1,
        product,
        isIncrease: true,
        additionalGuests: 0
      };
      let orderItem = this.orderItemService.buildOrderItem(increment, parentProduct, new Date(), undefined, []);
      orderItem = {
        ...orderItem,
        manageBookingType: manageType,
        manageMembership: {
          membershipId: membership.membershipId as number,
          existingBookingUniqueId: membership.bookingUniqueId,
          bookingItemPartId: setBookingItemPartId ? membership.bookingItemPartId : undefined
        }
      };

      orderItems.push(orderItem);
    });

    return orderItems;
  }

  private getUpgradeCost(
    item: IOrderItem,
    editMembershipItem: IOrderItem,
    upgrades: IMembershipUpgradesInfo[] = []
  ): number {
    const cost = upgrades.find(
      (upgrade: IMembershipUpgradesInfo) =>
        upgrade.upgradeProductId.toString() === item.productId &&
        editMembershipItem.manageMembership?.bookingItemPartId === upgrade.bookingItemPartId
    )?.upgradeAmount;

    if (!cost) {
      this.loggerService.warn(
        `Upgrade cost not found for bookingItemPartId ${editMembershipItem.manageMembership?.bookingItemPartId} productId ${item.productId}`
      );
      return 0;
    }

    return cost;
  }

  /**
   * Create a new order item from the membership order item we are editing/upgrading
   * Set the item cost to the new upgrade cost, and store the original cost in the manage details
   */
  private getUpgradedOrderItem(item: IOrderItem, editMembershipItem: IOrderItem, upgradeCost: number): IOrderItem {
    const manageMembership = editMembershipItem.manageMembership;
    if (!manageMembership) {
      return item;
    }

    return {
      ...item,
      uniqueId: editMembershipItem.uniqueId,
      relatedOrderItemKey: editMembershipItem.relatedOrderItemKey,
      manageBookingType: CMembership,
      cost: upgradeCost,
      isManageItemModified: true,
      manageMembership: {
        upgradeCost: item.cost,
        membershipId: manageMembership.membershipId,
        existingBookingUniqueId: manageMembership.existingBookingUniqueId,
        bookingItemPartId: manageMembership.bookingItemPartId
      }
    };
  }
}
